import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api, { getErrorMessage } from '../services/api';
import {
  AR_MANUAL_STATES,
  EXPEDIENT_TYPES,
  FINALIZED_STATUSES,
  expedientStatusLabel,
} from '@claimup/shared/constants/expedient';

/**
 * El estado de un expediente AR, cambiable a mano (docs/ar-estado-a-mano-plan.md):
 * la empresa, el admin, y el gestor si su empresa se lo deja. Quién puede lo dice
 * el servidor (`can_change_state`); a quien no, se le enseña el estado como antes.
 *
 * Los estados que cierran el expediente (bajas y cobrados) piden confirmación:
 * una baja cancela sus tareas.
 */
const DEACTIVATION_STATES = ['Fallido', 'Ilocalizable', 'Sin datos de contacto', 'Cancelled'];
const PLAN_DE_PAGOS = 'Plan de pagos';
const askFirst = s => DEACTIVATION_STATES.includes(s) || (FINALIZED_STATUSES || []).includes(s) || /^Cobrado/.test(s);

const Badge = ({ estado }) => (
  <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800'>
    {expedientStatusLabel(estado)}
  </span>
);

function ArStateSelect({ expedientId, expedientType, estado, onChange }) {
  const [canChange, setCanChange] = useState(false);
  const [pendiente, setPendiente] = useState(null);
  const [saving, setSaving] = useState(false);
  const isAr = expedientType === EXPEDIENT_TYPES.ACCOUNT_RECEIVABLE;

  useEffect(() => {
    if (!isAr) return undefined;
    let alive = true;
    api
      .get(`/ar-exit/expedients/${expedientId}`)
      .then(({ data }) => alive && setCanChange(Boolean(data?.data?.can_change_state)))
      .catch(() => alive && setCanChange(false));
    return () => {
      alive = false;
    };
  }, [expedientId, isAr]);

  if (!isAr || !canChange) return <Badge estado={estado} />;

  const enviar = async next => {
    setSaving(true);
    try {
      const { data } = await api.post(`/ar-exit/expedients/${expedientId}/state`, { estado: next });
      onChange?.(data.data.estado);
      toast.success(`Estado cambiado a ${expedientStatusLabel(data.data.estado)}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
      setPendiente(null);
    }
  };

  const elegir = e => {
    const next = e.target.value;
    if (next === estado) return;
    // Salir de «Plan de pagos» a mano no cancela el plan: se pregunta antes.
    if (askFirst(next) || estado === PLAN_DE_PAGOS) setPendiente(next);
    else enviar(next);
  };

  // Un estado viejo que ya no está en la lista se enseña igual, para no mentir.
  const opciones = AR_MANUAL_STATES.includes(estado) ? AR_MANUAL_STATES : [estado, ...AR_MANUAL_STATES];

  return (
    <div className='inline-flex flex-wrap items-center gap-2 text-sm'>
      <select
        aria-label='Estado del expediente'
        value={estado}
        disabled={saving || Boolean(pendiente)}
        onChange={elegir}
        className='rounded-lg border-blue-200 bg-blue-50 text-sm font-medium text-blue-800'
      >
        {opciones.map(s => (
          <option key={s} value={s}>
            {expedientStatusLabel(s)}
          </option>
        ))}
      </select>
      {pendiente && (
        <>
          <span className='text-slate-600'>
            ¿Pasar a <strong>{expedientStatusLabel(pendiente)}</strong>?
            {DEACTIVATION_STATES.includes(pendiente) && ' Se cancelan sus tareas.'}
            {estado === PLAN_DE_PAGOS &&
              ' El plan de pagos sigue vigente: si se ha caído, cancélalo en la pestaña «Plan de pagos».'}
          </span>
          <button
            type='button'
            disabled={saving}
            onClick={() => enviar(pendiente)}
            className='rounded-lg bg-blue-600 px-3 py-1 text-white disabled:opacity-50'
          >
            Confirmar
          </button>
          <button type='button' onClick={() => setPendiente(null)} className='text-slate-500 hover:underline'>
            Cancelar
          </button>
        </>
      )}
    </div>
  );
}

export default ArStateSelect;
